import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ArrowLeft, Store, Search, Filter, CheckCircle2, XCircle, Clock,
  Phone, Mail, MapPin, Truck, Calendar, RefreshCw, ChevronRight,
  AlertCircle, FileText, Send, Eye, MoreVertical, Building2
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import { API_URL } from '../../utils/apiConfig';

// Status badge component
function StatusBadge({ status }) {
  const statusConfig = {
    draft: { color: 'bg-slate-100 text-slate-700', label: 'Borrador' },
    pending_verification: { color: 'bg-yellow-100 text-yellow-700', label: 'Verificación Pendiente' },
    submitted: { color: 'bg-blue-100 text-blue-700', label: 'Enviada' },
    under_review: { color: 'bg-purple-100 text-purple-700', label: 'En Revisión' },
    pending_documents: { color: 'bg-orange-100 text-orange-700', label: 'Documentos Pendientes' },
    pending_approval: { color: 'bg-cyan-100 text-cyan-700', label: 'Aprobación Pendiente' },
    approved: { color: 'bg-emerald-100 text-emerald-700', label: 'Aprobada' },
    active: { color: 'bg-green-100 text-green-700', label: 'Activa' },
    rejected: { color: 'bg-red-100 text-red-700', label: 'Rechazada' }
  };
  
  const config = statusConfig[status] || { color: 'bg-slate-100 text-slate-700', label: status };
  
  return (
    <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${config.color}`}>
      {config.label}
    </span>
  );
}

export default function AdminApplicationReview() {
  const navigate = useNavigate();
  const getToken = () => localStorage.getItem('pecash_token');
  
  // State
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [applications, setApplications] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedApp, setSelectedApp] = useState(null);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [processing, setProcessing] = useState(false);
  
  // Filters
  const [statusFilter, setStatusFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Review form
  const [decision, setDecision] = useState('');
  const [notes, setNotes] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');

  // Fetch stats
  const fetchStats = useCallback(async () => {
    const token = getToken();
    try {
      const response = await fetch(`${API_URL}/api/admin/merchant-applications/stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  }, []);

  // Fetch applications
  const fetchApplications = useCallback(async () => {
    const token = getToken();
    setLoading(true);
    try {
      let url = `${API_URL}/api/admin/merchant-applications/list?limit=50`;
      if (statusFilter) url += `&status=${statusFilter}`;
      if (searchQuery) url += `&search=${encodeURIComponent(searchQuery)}`;
      
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setApplications(data.applications);
        setTotalCount(data.total);
      }
    } catch (err) {
      console.error('Error fetching applications:', err);
      toast.error('Error loading applications');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, searchQuery]);

  // Initial load
  useEffect(() => {
    fetchStats();
    fetchApplications();
  }, [fetchStats, fetchApplications]);

  // Review application
  const handleReview = async () => {
    if (!decision) {
      toast.error('Please select a decision');
      return;
    }
    
    if (decision === 'reject' && !rejectionReason) {
      toast.error('Rejection reason is required');
      return;
    }
    
    const token = getToken();
    setProcessing(true);
    
    try {
      const response = await fetch(`${API_URL}/api/admin/merchant-applications/${selectedApp.id}/review`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          decision,
          notes: notes || null,
          rejection_reason: decision === 'reject' ? rejectionReason : null
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        toast.success(decision === 'approve' 
          ? `${selectedApp.business_name} approved! ${data.email_sent ? 'Welcome email sent.' : ''}`
          : `Application rejected.`
        );
        setShowReviewModal(false);
        setSelectedApp(null);
        setDecision('');
        setNotes('');
        setRejectionReason('');
        fetchStats();
        fetchApplications();
      } else {
        toast.error(data.error || 'Failed to process review');
      }
    } catch (err) {
      console.error('Error:', err);
      toast.error('Error processing review');
    } finally {
      setProcessing(false);
    }
  };

  // Start review
  const handleStartReview = async (app) => {
    const token = getToken();
    try {
      const response = await fetch(`${API_URL}/api/admin/merchant-applications/${app.id}/start-review`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast.success('Review started');
        fetchApplications();
      }
    } catch (err) {
      console.error('Error:', err);
    }
  };

  // Resend welcome email
  const handleResendEmail = async (app) => {
    const token = getToken();
    try {
      const response = await fetch(`${API_URL}/api/admin/merchant-applications/${app.id}/resend-welcome-email`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const data = await response.json();
      
      if (data.success) {
        toast.success('Welcome email sent');
      } else {
        toast.error(data.error || 'Failed to send email');
      }
    } catch (err) {
      console.error('Error:', err);
      toast.error('Error sending email');
    }
  };

  return (
    <div className="min-h-screen bg-slate-900 pb-20">
      {/* Header */}
      <header className="bg-slate-800 border-b border-slate-700 px-6 py-4 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/admin/dashboard')}
              className="text-slate-400 hover:text-white"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Back
            </Button>
            <div className="h-6 w-px bg-slate-700" />
            <div className="w-10 h-10 rounded-xl bg-amber-600 flex items-center justify-center">
              <Store className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-white">Merchant Applications</h1>
              <p className="text-sm text-slate-400">Review & approve colmado registrations</p>
            </div>
          </div>
          
          <Button
            onClick={() => { fetchStats(); fetchApplications(); }}
            variant="ghost"
            className="text-slate-400 hover:text-white"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
      </header>

      {/* Stats Cards */}
      {stats && (
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-slate-800 border border-slate-700 rounded-xl p-4">
              <p className="text-slate-400 text-sm">Total Applications</p>
              <p className="text-2xl font-bold text-white">{stats.total}</p>
            </div>
            <div className="bg-slate-800 border border-blue-700/50 rounded-xl p-4">
              <p className="text-blue-400 text-sm">Pending Review</p>
              <p className="text-2xl font-bold text-blue-400">{stats.pending_review}</p>
            </div>
            <div className="bg-slate-800 border border-emerald-700/50 rounded-xl p-4">
              <p className="text-emerald-400 text-sm">Approved</p>
              <p className="text-2xl font-bold text-emerald-400">{stats.approved}</p>
            </div>
            <div className="bg-slate-800 border border-red-700/50 rounded-xl p-4">
              <p className="text-red-400 text-sm">Rejected</p>
              <p className="text-2xl font-bold text-red-400">{stats.rejected}</p>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="max-w-7xl mx-auto px-6 mb-6">
        <div className="flex flex-wrap gap-4">
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by name or phone..."
                className="pl-10 bg-slate-800 border-slate-700 text-white"
              />
            </div>
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white"
          >
            <option value="">All Statuses</option>
            <option value="submitted">Submitted</option>
            <option value="under_review">Under Review</option>
            <option value="pending_documents">Pending Documents</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
          </select>
        </div>
      </div>

      {/* Applications List */}
      <div className="max-w-7xl mx-auto px-6">
        {loading ? (
          <div className="text-center py-12">
            <RefreshCw className="w-8 h-8 text-purple-400 animate-spin mx-auto mb-4" />
            <p className="text-slate-400">Loading applications...</p>
          </div>
        ) : applications.length === 0 ? (
          <div className="text-center py-12 bg-slate-800 rounded-xl border border-slate-700">
            <Store className="w-12 h-12 text-slate-600 mx-auto mb-4" />
            <p className="text-slate-400">No applications found</p>
          </div>
        ) : (
          <div className="space-y-4">
            {applications.map((app) => (
              <motion.div
                key={app.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-slate-800 border border-slate-700 rounded-xl p-4 hover:border-slate-600 transition-colors"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-white font-semibold">{app.business_name}</h3>
                      <StatusBadge status={app.status} />
                    </div>
                    
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <div className="flex items-center gap-2 text-slate-400">
                        <Building2 className="w-4 h-4" />
                        {app.owner_name}
                      </div>
                      <div className="flex items-center gap-2 text-slate-400">
                        <Phone className="w-4 h-4" />
                        {app.phone}
                      </div>
                      <div className="flex items-center gap-2 text-slate-400">
                        <MapPin className="w-4 h-4" />
                        {app.location_city}
                      </div>
                      <div className="flex items-center gap-2 text-slate-400">
                        <Calendar className="w-4 h-4" />
                        {new Date(app.created_at).toLocaleDateString()}
                      </div>
                    </div>
                    
                    {app.email && (
                      <div className="flex items-center gap-2 text-slate-500 text-sm mt-2">
                        <Mail className="w-4 h-4" />
                        {app.email}
                      </div>
                    )}
                    
                    <p className="text-slate-600 text-xs mt-2 font-mono">{app.id}</p>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    {app.status === 'submitted' && (
                      <Button
                        size="sm"
                        onClick={() => handleStartReview(app)}
                        className="bg-purple-600 hover:bg-purple-700"
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        Start Review
                      </Button>
                    )}
                    
                    {app.status === 'under_review' && (
                      <Button
                        size="sm"
                        onClick={() => { setSelectedApp(app); setShowReviewModal(true); }}
                        className="bg-emerald-600 hover:bg-emerald-700"
                      >
                        <CheckCircle2 className="w-4 h-4 mr-1" />
                        Review
                      </Button>
                    )}
                    
                    {app.status === 'approved' && app.email && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleResendEmail(app)}
                        className="border-slate-600 text-slate-300"
                      >
                        <Send className="w-4 h-4 mr-1" />
                        Resend Email
                      </Button>
                    )}
                    
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => { setSelectedApp(app); }}
                      className="text-slate-400"
                    >
                      <MoreVertical className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      {/* Review Modal */}
      <AnimatePresence>
        {showReviewModal && selectedApp && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4"
            onClick={() => setShowReviewModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-slate-800 rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto"
            >
              <div className="p-6 border-b border-slate-700">
                <h2 className="text-xl font-bold text-white">Review Application</h2>
                <p className="text-slate-400 text-sm">{selectedApp.business_name}</p>
              </div>
              
              <div className="p-6 space-y-4">
                {/* Application Details */}
                <div className="bg-slate-700/50 rounded-lg p-4 space-y-2">
                  <div className="flex justify-between">
                    <span className="text-slate-400">Owner</span>
                    <span className="text-white">{selectedApp.owner_name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">Phone</span>
                    <span className="text-white">{selectedApp.phone}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">City</span>
                    <span className="text-white">{selectedApp.location_city}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">Delivery</span>
                    <span className="text-white">{selectedApp.has_delivery ? 'Yes' : 'No'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">Verified</span>
                    <span className={selectedApp.phone_verified ? 'text-emerald-400' : 'text-red-400'}>
                      {selectedApp.phone_verified ? 'Yes' : 'No'}
                    </span>
                  </div>
                </div>
                
                {/* Decision */}
                <div>
                  <label className="block text-sm text-slate-400 mb-2">Decision</label>
                  <div className="flex gap-3">
                    <Button
                      onClick={() => setDecision('approve')}
                      className={`flex-1 ${decision === 'approve' ? 'bg-emerald-600' : 'bg-slate-700'}`}
                    >
                      <CheckCircle2 className="w-4 h-4 mr-2" />
                      Approve
                    </Button>
                    <Button
                      onClick={() => setDecision('reject')}
                      className={`flex-1 ${decision === 'reject' ? 'bg-red-600' : 'bg-slate-700'}`}
                    >
                      <XCircle className="w-4 h-4 mr-2" />
                      Reject
                    </Button>
                  </div>
                </div>
                
                {/* Notes */}
                <div>
                  <label className="block text-sm text-slate-400 mb-2">Admin Notes (optional)</label>
                  <textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
                    rows={2}
                    placeholder="Internal notes..."
                  />
                </div>
                
                {/* Rejection Reason */}
                {decision === 'reject' && (
                  <div>
                    <label className="block text-sm text-slate-400 mb-2">Rejection Reason *</label>
                    <textarea
                      value={rejectionReason}
                      onChange={(e) => setRejectionReason(e.target.value)}
                      className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
                      rows={2}
                      placeholder="Reason for rejection..."
                    />
                  </div>
                )}
                
                {/* Actions */}
                <div className="flex gap-3 pt-4">
                  <Button
                    variant="ghost"
                    onClick={() => setShowReviewModal(false)}
                    className="flex-1 text-slate-400"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleReview}
                    disabled={processing || !decision}
                    className={`flex-1 ${decision === 'approve' ? 'bg-emerald-600 hover:bg-emerald-700' : decision === 'reject' ? 'bg-red-600 hover:bg-red-700' : 'bg-slate-600'}`}
                  >
                    {processing ? 'Processing...' : 'Submit Review'}
                  </Button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
